import type { IStringTool } from "../core/IStringTool.ts"
import type { StringColor } from "../models/color/StringColor.ts"

/**
 * Input parameters for `LerpColorTool`.
 */
interface LerpColorInput {
  /**
   * Starting color as an object `{ r, g, b, a }` where each value is in the range `0–1`.
   */
  from: StringColor

  /**
   * Target color as an object `{ r, g, b, a }` where each value is in the range `0–1`.
   */
  to: StringColor

  /**
   * Interpolation progress from `0` (start) to `1` (end).
   */
  progress: number
}

/**
 * Tool for linearly interpolating between two RGBA colors using `StringColor` format.
 * Each channel (`r`, `g`, `b`, `a`) is interpolated independently.
 * Returns a new `StringColor` with the interpolated values.
 */
export default class LerpColorTool implements IStringTool<LerpColorInput, StringColor> {
  /**
   * Performs linear interpolation between two `StringColor` values.
   *
   * @param input.from - The starting color `{ r, g, b, a }`.
   * @param input.to - The target color `{ r, g, b, a }`.
   * @param input.progress - A number from `0` to `1` indicating interpolation progress.
   * @returns Interpolated color as a new `StringColor`.
   */
  process({ from, to, progress }: LerpColorInput): StringColor {
    return {
      r: from.r + (to.r - from.r) * progress,
      g: from.g + (to.g - from.g) * progress,
      b: from.b + (to.b - from.b) * progress,
      a: from.a + (to.a - from.a) * progress,
    }
  }
}
